import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { getSettings } from '../../services/SettingsService';
import { getActiveSessions } from '../../services/TodayService';
import logger from '../../utils/logger';

const DISMISS_KEY = 'goldhub_onboarding_dismissed';

/**
 * First-run setup checklist, shown at the top of the Dashboard while incomplete.
 * Dismissible (persisted in localStorage). Steps:
 *   1. Connect Binance API keys  — done if settings expose a saved spot access key.
 *   2. Try a paper trade         — done if any trading session exists.
 *   3. Review safety (2FA, kill) — informational, always actionable.
 */
function SetupChecklist() {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const history = useHistory();

    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISS_KEY) === 'true'; } catch (e) { return false; }
    });
    const [hasKeys, setHasKeys] = useState(false);
    const [hasSession, setHasSession] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (dismissed) return;
        let mounted = true;

        getSettings()
            .then(s => {
                if (!mounted) return;
                // A saved spot key (masked or raw) means keys are connected.
                setHasKeys(!!(s && s.accessKey && String(s.accessKey).length > 0));
            })
            .catch(err => logger.error(err.response ? err.response.data : err.message));

        getActiveSessions()
            .then(list => { if (mounted) setHasSession(Array.isArray(list) && list.length > 0); })
            .catch(err => logger.error(err.response ? err.response.data : err.message))
            .finally(() => { if (mounted) setLoaded(true); });

        return () => { mounted = false; };
    }, [dismissed]);

    if (dismissed) return null;

    const steps = [
        {
            done: hasKeys,
            title: t('onboarding.step.keys'),
            text: t('onboarding.step.keysHint'),
            cta: t('onboarding.step.keysCta'),
            to: '/settings'
        },
        {
            done: hasSession,
            title: t('onboarding.step.paper'),
            text: t('onboarding.step.paperHint'),
            cta: t('onboarding.step.paperCta'),
            to: '/today'
        },
        {
            done: false,
            title: t('onboarding.step.safety'),
            text: t('onboarding.step.safetyHint'),
            cta: t('onboarding.step.safetyCta'),
            to: '/settings'
        }
    ];

    const doneCount = steps.filter(s => s.done).length;
    // The safety step is informational, so "complete" = the two data-driven steps.
    const complete = hasKeys && hasSession;

    // Hide once the actionable steps are done (and data has loaded so we don't
    // flash the checklist before knowing the real state).
    if (loaded && complete) return null;

    function onDismiss() {
        try { localStorage.setItem(DISMISS_KEY, 'true'); } catch (e) { /* ignore */ }
        setDismissed(true);
    }

    const Check = () => (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className="gh-pos" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42l2.79 2.8 6.79-6.8a1 1 0 011.42 0z" clipRule="evenodd" />
        </svg>
    );
    const Box = () => (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" className="text-muted" xmlns="http://www.w3.org/2000/svg">
            <rect x="3.5" y="3.5" width="13" height="13" rx="2.5" strokeWidth="1.5" />
        </svg>
    );

    return (
        <div className="card border-0 shadow mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                    <h2 className="fs-5 fw-bold mb-0">{t('onboarding.title')}</h2>
                    <small className="text-muted">{t('onboarding.subtitle')}</small>
                </div>
                <div className="d-flex align-items-center">
                    <span className="gh-badge gh-badge-gold me-3 num">{doneCount}/{steps.length}</span>
                    <button type="button" className="btn-close" aria-label={t('onboarding.dismiss')} title={t('onboarding.dismiss')} onClick={onDismiss} />
                </div>
            </div>
            <div className="card-body">
                <div className="row g-3">
                    {steps.map((step, i) => (
                        <div className="col-12 col-md-4" key={i}>
                            <div className={`gh-stat h-100 ${step.done ? '' : ''}`} style={step.done ? { opacity: 0.75 } : undefined}>
                                <div className="d-flex align-items-start mb-2">
                                    <span className="me-2">{step.done ? <Check /> : <Box />}</span>
                                    <span className={`fw-bold ${step.done ? 'text-decoration-line-through text-muted' : ''}`}>{step.title}</span>
                                </div>
                                <div className="gh-stat-sub mb-3">{step.text}</div>
                                {!step.done && (
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => history.push(step.to)}>
                                        {step.cta}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default SetupChecklist;
